"use client";

import { useCallback } from "react";
import { formatNumberAsString } from "@cede/utils";
import { isValidNumber, sanitizeValue } from "@/utils/trade";

/**
 * Configuration for percentage amount calculation
 */
export interface PercentageAmountConfig {
  /** Balance as a string (may include formatting like spaces) */
  balance: string | undefined;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is read-only */
  readOnly?: boolean;
  /** Callback to set the calculated amount */
  onAmountChange: (value: string) => void;
  /** Optional callback when focus should be triggered (e.g., to compute trade) */
  onFocus?: () => void;
  /**
   * Amount to reserve from the balance (e.g., gas for native tokens).
   * Applied to ALL percentages, not just Max.
   */
  reserve?: number;
  /** Number of decimal places for the output (default: auto) */
  decimals?: number;
  /**
   * Custom formatting function for the output amount.
   * If not provided, uses formatNumberAsString.
   */
  formatOutput?: (amount: string) => string;
}

/**
 * Result of the usePercentageAmount hook
 */
export interface PercentageAmountResult {
  /** Handler for percentage button clicks (0.25, 0.5, 0.75, 1) */
  handlePercentageClick: (percentage: number) => void;
  /** Handler for Max click (equivalent to handlePercentageClick(1)) */
  handleMaxClick: () => void;
}

/**
 * Hook for unified percentage amount calculations across trading widgets.
 *
 * Features:
 * - Consistent percentage calculation logic
 * - Optional reserve amount (e.g., gas reserve for native tokens)
 * - Configurable decimal precision
 * - Custom output formatting
 *
 * @example
 * // Basic usage (CEX/Hyperliquid)
 * const { handlePercentageClick, handleMaxClick } = usePercentageAmount({
 *   balance: "1,234.56",
 *   onAmountChange: setAmount,
 * });
 *
 * @example
 * // DEX usage with gas reserve
 * const { handlePercentageClick, handleMaxClick } = usePercentageAmount({
 *   balance: "0.5",
 *   onAmountChange: setAmount,
 *   reserve: isNativeToken ? 0.0005 : 0, // Gas reserve for ETH
 *   decimals: 18,
 * });
 */
export function usePercentageAmount({
  balance,
  disabled = false,
  readOnly = false,
  onAmountChange,
  onFocus,
  reserve = 0,
  decimals,
  formatOutput,
}: PercentageAmountConfig): PercentageAmountResult {
  const handlePercentageClick = useCallback(
    (percentage: number) => {
      if (!balance || disabled || readOnly) return;

      // Parse balance (remove formatting like commas and spaces)
      const balanceNum = parseFloat(balance.replace(/[,\s]/g, ""));
      if (isNaN(balanceNum) || balanceNum <= 0) return;

      // Apply reserve to get effective balance (for gas reserves, etc.)
      const effectiveBalance = Math.max(0, balanceNum - reserve);
      if (effectiveBalance <= 0) return;

      // Calculate the percentage amount
      const calculatedAmount = effectiveBalance * percentage;

      // Format the output
      let formattedAmount: string;
      if (decimals !== undefined) {
        // Use fixed decimals
        formattedAmount = calculatedAmount.toFixed(decimals);
      } else {
        // Use default formatting with sanitization
        const sanitized = sanitizeValue(calculatedAmount.toString());
        formattedAmount = isValidNumber(sanitized) ? formatNumberAsString(sanitized) : calculatedAmount.toString();
      }

      // Apply custom formatting if provided
      if (formatOutput) {
        formattedAmount = formatOutput(formattedAmount);
      }

      onAmountChange(formattedAmount);
      onFocus?.();
    },
    [balance, disabled, readOnly, reserve, decimals, formatOutput, onAmountChange, onFocus],
  );

  const handleMaxClick = useCallback(() => {
    handlePercentageClick(1);
  }, [handlePercentageClick]);

  return {
    handlePercentageClick,
    handleMaxClick,
  };
}

export default usePercentageAmount;
